import { useState } from 'react';
import { FilterRow, Chip, Modal, PickerChip } from '@/components/kit';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { currentMonth, monthChip, monthLabel, shiftMonth } from '../lib/scope';

/**
 * THE FILTER ROW EVERY MONTHLY REPORT WEARS — two month chips, the branch
 * scope, and the comparison month.
 *
 * "vs Jul ▾" has an arrow, so it OPENS SOMETHING (BRIEF §4.8). It used to be
 * the kind of control that looks like a dropdown and is a label; here it opens
 * a real picker of the twelve months before the one being read, and choosing
 * one changes every figure on the screen that carries a comparison.
 */
export function MonthFilterRow({
  month,
  onMonth,
  compare,
  onCompare,
  branchLine,
  extra,
  wrapOnPhone,
}: {
  /** The month being read, "YYYY-MM". */
  month: string;
  onMonth: (next: string) => void;
  /** The month it is measured against. */
  compare: string;
  onCompare: (next: string) => void;
  /** What the clinic's branch scope actually is. */
  branchLine: string;
  /** Filters this report adds between the scope and the comparison. */
  extra?: React.ReactNode;
  /** Phone: let the chips wrap onto a second line instead of scrolling sideways. */
  wrapOnPhone?: boolean;
}) {
  const [picking, setPicking] = useState(false);
  /* THE TWO CHIPS ARE THE LAST COMPLETE MONTH AND THE ONE BEING LIVED, in that
     order — the only two a manager moves between on a monthly report, and the
     pair the canvas draws ("Aug 2026 · Sep 2026"). Anything further back is a
     comparison, which is the control beside them. */
  const later = currentMonth();
  const earlier = shiftMonth(later, -1);
  const options = Array.from({ length: 12 }, (_, i) => shiftMonth(month, -(i + 1)));
  const narrow = useNarrowViewport();

  return (
    <>
      {narrow ? (
        /* THE PHONE BOARDS CARRY THE MONTH IN THE STRIP'S TITLE ("Revenue ·
           Aug"), so the row shrinks to the two controls that change it: which
           month, and against which. The branch line is the desk's. */
        <FilterRow style={wrapOnPhone ? { flexWrap: 'wrap', overflowX: 'visible' } : undefined}>
          <PickerChip
            kind="brand"
            label={`${monthChip(month)} ▾`}
            ariaLabel="Month"
            value={month}
            onChange={onMonth}
            options={Array.from({ length: 12 }, (_, i) => {
              const m = shiftMonth(later, -i);
              return { value: m, label: monthLabel(m) };
            })}
          />
          {extra}
          <Chip kind="outline" small onClick={() => setPicking(true)}>
            vs {monthChip(compare).split(' ')[0]} ▾
          </Chip>
        </FilterRow>
      ) : (
      <FilterRow>
        <Chip kind={month === earlier ? 'brand' : 'outline'} small className={month === earlier ? 'w6' : undefined}
          onClick={month === earlier ? undefined : () => onMonth(earlier)}>
          {monthChip(earlier)}
        </Chip>
        <Chip kind={month === later ? 'brand' : 'outline'} small className={month === later ? 'w6' : undefined}
          onClick={month === later ? undefined : () => onMonth(later)}>
          {monthChip(later)}
        </Chip>
        <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
        <span className="t-sec mut">{branchLine}</span>
        <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
        {extra}
        <Chip kind="outline" small onClick={() => setPicking(true)}>
          vs {monthChip(compare).split(' ')[0]} ▾
        </Chip>
      </FilterRow>
      )}

      <Modal
        open={picking}
        onClose={() => setPicking(false)}
        title="Compare against"
        sub={`every figure on this screen is measured against the month you pick — ${monthLabel(month)} is what it is measuring`}
        width={420}
        foot={
          <button type="button" className="btn btn-outline" onClick={() => setPicking(false)}>Close</button>
        }
      >
        <div className="dmenu" style={{ position: 'static', boxShadow: 'none', border: 'none', minWidth: 0, padding: '10px 0 0' }}>
          {options.map((m) => (
            <button key={m} type="button" className="mi"
              onClick={() => { onCompare(m); setPicking(false); }}>
              <span>{monthLabel(m)}</span>
              {m === compare ? <span className="t-cap brand w6">comparing</span> : null}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}

